import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

let settingsCache = null;

/**
 * Custom hook to fetch site settings from the backend API.
 * Returns a key-value map of settings.
 *
 * @returns {{ settings: Object, loading: boolean, getSetting: Function }}
 *
 * Usage:
 *   const { getSetting } = useSiteSettings();
 *   const phone = getSetting('company_phone', '+91 98854 94242');
 */
export default function useSiteSettings() {
  const [settings, setSettings] = useState(settingsCache || {});
  const [loading, setLoading] = useState(!settingsCache);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (settingsCache) {
      setSettings(settingsCache);
      setLoading(false);
      return;
    }

    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        const map = {};
        (res.data || []).forEach(item => {
          map[item.key] = item.value;
          if (item.value_json) {
            map[`${item.key}_json`] = item.value_json;
          }
        });
        settingsCache = map;
        setSettings(map);
      } catch (err) {
        console.warn('[useSiteSettings] Failed to fetch settings:', err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const getSetting = (key, defaultValue = '') => {
    return settings[key] || defaultValue;
  };

  return { settings, loading, getSetting };
}
